import React, { useEffect, useState } from "react";
import { Modal, Table, message, Space, Button, Tooltip } from "antd";
import {
    AppstoreOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    OrderedListOutlined
} from "@ant-design/icons";
import axios from "axios";
import { getConfig } from "../../../../../config";
import TransferOrderCardEditWizard from "./TransferOrderCardEditWizard";
import ListItem from "antd/es/upload/UploadList/ListItem";

const { API_BASE_URL } = getConfig();

interface TransferOrderCardMainProps {
    visible: boolean;
    onClose: () => void;
    onCompletion: () => void;
    selectedProject: string;
}

const TransferOrderCardMain: React.FC<TransferOrderCardMainProps> = ({ visible, onClose, onCompletion, selectedProject }) => {
    const [data, setData] = useState<OrderCardOverviewData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditWizardVisible, setIsEditWizardVisible] = useState(false);
    const [selectedOrderCard, setSelectedOrderCard] = useState<OrderCardOverviewData | null>(null);
    const [selectedTasks, setSelectedTasks] = useState<LegacyTask[]>([]);
    const [goodsReceivingCards, setGoodsReceivingCards] = useState<OrderCardOverviewData[]>([]);
    const [measureAssignmentCards, setMeasureAssignmentCards] = useState<OrderCardOverviewData[]>([]);
    const [isTaskSelectionVisible, setIsTaskSelectionVisible] = useState(false);

    useEffect(() => {
        if (visible && selectedProject) {
            fetchData(selectedProject);
        }
    }, [visible, selectedProject]);

    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const response = await axios.get<OrderCardOverviewData[]>(
                `${API_BASE_URL}/getLegacyOrderCardData?projectNo=${projectNo}`
            );
            setData(response.data);
            const goodsReceiving: OrderCardOverviewData[] = [];
            const measureAssignment: OrderCardOverviewData[] = [];

            response.data.forEach((order) => {
                order.tasks.forEach((task) => {
                    if (order.statusDone === false) {
                        if (task.code === "CONSTRUCTION_GOODS_RECEIVING") {
                            goodsReceiving.push(order);
                        }
                        if (task.code === "CONSTRUCTION_MEASURE_ASSIGNMENT") {
                            measureAssignment.push(order);
                        }
                    }
                });
            });

            setGoodsReceivingCards(goodsReceiving);
            setMeasureAssignmentCards(measureAssignment);
        } catch (error) {
            console.error("Error fetching order card data:", error);
            message.error("Fehler beim Laden der Auftragskarten-Daten.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGroupedAction = (taskType: string, affectedCards: OrderCardOverviewData[]) => {
        if (affectedCards.length === 0) return;

        const actionText = taskType === "CONSTRUCTION_GOODS_RECEIVING"
            ? "Willst Du den Konstruktion-Wareneingang für folgende Karten buchen?"
            : "Willst Du die Bemassung für folgende Karten beauftragen?";
        const apiEndpoint = taskType === "CONSTRUCTION_GOODS_RECEIVING"
            ? "saveTransferProjectOrderCardGoodsReceiving"
            : "saveTransferProjectOrderCardGoodsReceiving";

        Modal.confirm({
            width: 600,
            title: actionText,
            content: (
                <ul>
                    {affectedCards.map((card, index) => (
                        <li key={index}>{card.cardName}</li>
                    ))}
                </ul>
            ),
            okText: "Ja",
            cancelText: "Abbrechen",
            onOk: async () => {
                try {
                    await Promise.all(affectedCards.map((card) =>
                        axios.get(`${API_BASE_URL}/${apiEndpoint}`, {
                            params: {
                                projectNo: selectedProject,
                                orderCardId: card.cardCode,
                                jobCode: card.productCode,
                                taskType: taskType
                            }
                        })
                    ));
                    message.success("Aktion erfolgreich durchgeführt.");
                    fetchData(selectedProject);
                } catch (error) {
                    message.error("Fehler beim Durchführen der Aktion.");
                }
            },
        });
    };

    const handleEdit = (orderCard: OrderCardOverviewData) => {
        console.log("Edit clicked for:", orderCard); // Debugging
        if (orderCard.allowAllTasks) {
            console.log("Opening Task Selection Modal for Order Card:", orderCard.cardName);
            setSelectedOrderCard(orderCard);
            setIsTaskSelectionVisible(true);
        } else {
            if (orderCard.tasks.length === 1 &&
                (orderCard.tasks[0].code === "CONSTRUCTION_GOODS_RECEIVING" ||
                    orderCard.tasks[0].code === "CONSTRUCTION_MEASURE_ASSIGNMENT")) {
                handleGroupedAction(orderCard.tasks[0].code, [orderCard]);
                return;
            }
            setSelectedOrderCard(orderCard);
            setSelectedTasks(orderCard.tasks);
            setIsEditWizardVisible(true);
        }
    };

    const handleTaskSelection = (task: LegacyTask) => {
        let code = task.code;
        if (code === "MEASURING_OPEN" && selectedOrderCard) {
            Modal.confirm({
                title: "Ist das Messen wirklich noch offen?",
                okText: "Ja",
                cancelText: "Abbrechen",
                onOk: async () => {
                    try {
                        await axios.get(`${API_BASE_URL}/saveOpenNetworkplanStatus`, {
                            params: {
                                projectNo: selectedProject,
                                productCode: selectedOrderCard.productCode,
                                taskType: code,
                                orderCardId: selectedOrderCard.cardCode,
                            }
                        });
                        message.success("Status wurde erfolgreich gesetzt.");
                        fetchData(selectedProject);
                    } catch (error) {
                        message.error("Fehler beim Buchen des Status.");
                    } finally {
                        setIsTaskSelectionVisible(false);
                    }
                },
            });
            return;
        }
        console.log("Selected Task:", task); // Debugging
        setSelectedOrderCard((prev) => prev || selectedOrderCard);
        setSelectedTasks([task]);
        setIsTaskSelectionVisible(false);

        setTimeout(() => {
            console.log("Opening Wizard with selectedOrderCard:", selectedOrderCard, "and selectedTasks:", selectedTasks); // Debugging
            setIsEditWizardVisible(true);
        }, 200);
    };


    const handleWizardClose = () => {
        setIsEditWizardVisible(false);
        setSelectedOrderCard(null);
        setSelectedTasks([]);
        fetchData(selectedProject);
    };

    const columns = [
        {
            title: "Status",
            dataIndex: "statusDone",
            key: "statusDone",
            width: 80,
            render: (statusDone: boolean) => (
                statusDone ? <CheckCircleOutlined style={{ display: "flex", justifyContent: "center", color: "green", fontSize: "18px" }} />
                    : <CloseCircleOutlined style={{ display: "flex", justifyContent: "center", color: "red", fontSize: "18px" }} />
            ),
        },
        {
            title: "Kartenname",
            dataIndex: "cardName",
            key: "cardName",
            width: 350,
            sorter: (a: OrderCardOverviewData, b: OrderCardOverviewData) => a.cardName.localeCompare(b.cardName)
        },
        {
            title: "Produkt",
            dataIndex: "productCode",
            key: "productCode",
            width: 150,
            sorter: (a: OrderCardOverviewData, b: OrderCardOverviewData) => a.productCode.localeCompare(b.productCode)
        },
        {
            title: "Status",
            dataIndex: "statusMin",
            key: "status",
            width: 100,
            render: (_: string, record: OrderCardOverviewData) => (
                <span>{record.statusMin}</span>
            ),
        },
        {
            title: "Optionen / Aktueller Status",
            dataIndex: "tasks",
            key: "taskType",
            width: 350,
            render: (tasks: LegacyTask[]) => (
                <div>
                    {tasks.map((task, index) => (
                        <div key={index}>
                            {task.description}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: "",
            dataIndex: "cardName",
            key: "edit",
            className: "righted-column",
            width: 100,
            render: (cardName: string, record: OrderCardOverviewData) => (
                <div style={{display: "flex", justifyContent: "flex-end"}}>
                    <Tooltip title="Bearbeiten">
                        <Button
                            disabled={!record.editable}
                            icon={record.allowAllTasks ? <OrderedListOutlined/> : <EditOutlined/>}
                            onClick={() => handleEdit(record)}
                            type="primary"
                        />
                    </Tooltip>
                </div>
            ),
        }
    ];

    return (
        <>
        <Modal
                title="Wähle eine Option"
                visible={isTaskSelectionVisible}
                onCancel={() => setIsTaskSelectionVisible(false)}
                footer={null}
            >
                {selectedOrderCard?.tasks.map((task) => (
                    <Button
                        key={task.code}
                        style={{ width: "100%", marginBottom: "8px" }}
                        onClick={() => handleTaskSelection(task)}
                    >
                        {task.description}
                    </Button>
                ))}
            </Modal>
            <Modal
                title={`Auftragskarten im Projekt ${selectedProject}`}
                visible={visible}
                onCancel={onClose}
                footer={null}
                width="70vw"
                style={{ top: "0.0vh" }}
                styles={{
                    body: { height: "85vh", overflowY: "auto", padding: "20px" },
                    content: { top: "2.5vh", height: "95vh" }
                }}
            >
                <Space style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {goodsReceivingCards.length > 0 && (
                        <Button
                            type="primary"
                            onClick={() => handleGroupedAction("CONSTRUCTION_GOODS_RECEIVING", goodsReceivingCards)}
                        >
                            Konstruktion-Wareneingang buchen ({goodsReceivingCards.length})
                        </Button>
                    )}
                    {measureAssignmentCards.length > 0 && (
                        <Button
                            type="primary"
                            onClick={() => handleGroupedAction("CONSTRUCTION_MEASURE_ASSIGNMENT", measureAssignmentCards)}
                        >
                            Bemassung beauftragen ({measureAssignmentCards.length})
                        </Button>
                    )}
                </Space>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="cardName"
                    pagination={{ pageSize: 100 }}
                    size="middle"
                    loading={isLoading}
                />
            </Modal>
            {selectedOrderCard && selectedTasks.length > 0 && (
                <TransferOrderCardEditWizard
                    visible={isEditWizardVisible}
                    onClose={handleWizardClose}
                    selectedOrderCard={selectedOrderCard}
                    selectedProject={selectedProject}
                    tasks={selectedTasks}
                />
            )}
        </>
    );
};

export default TransferOrderCardMain;
